import React from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import { ReactComponent as AIStarsIcon } from "src/images/ai-stars.svg";
import { renderMarkdown } from "../../utils/renderMarkdown";
import ThinkingBlock from "../ThinkingBlock/ThinkingBlock";
import Attachment from "src/app/Task/Attachments/Attachment";
import type { AttachmentMetaData } from "src/app/Task/Attachments/Attachment";
import { AttachmentPlacement, TaskType } from "src/types";
import { REGEX_BY_PROVIDER } from "src/types/generic";
import type { WsMemberSelectedContext } from "src/redux/ai-chat/aiChatTypes";
import SelectedContextBadge from "../SelectedContextBadge/SelectedContextBadge";
import { RootState } from "src/redux/reducers";
import { isInternalServiceUrl } from "src/utils/internalServices";

import "./ChatMessage.scss";

/**
 * Check if the content contains any social profile links that can be
 * searched in Creator Search (Instagram, TikTok, Snapchat, YouTube, etc.).
 */
export function hasSocialProfileLinks(content: string): boolean {
  if (!content) return false;
  return Object.values(REGEX_BY_PROVIDER).some((regex) => regex.test(content));
}

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  thinking?: string;
  thinkingSeconds?: number;
  isStreaming?: boolean;
  timestamp?: string;
  attachedFiles?: AttachmentMetaData[];
  selectedItems?: WsMemberSelectedContext;
  showQuickPrompt?: boolean;
  onQuickPrompt?: () => void;
  onHelpCenterLinkClick?: (url: string) => void;
}

export default function ChatMessage({
  role,
  content,
  thinking,
  thinkingSeconds,
  isStreaming = false,
  timestamp,
  attachedFiles,
  selectedItems,
  showQuickPrompt = false,
  onQuickPrompt,
  onHelpCenterLinkClick,
}: ChatMessageProps) {
  const history = useHistory();
  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );
  const memberUuid = identity?.id;
  const hasFiles = Boolean(attachedFiles && attachedFiles.length > 0);
  const hasSelectedItems = Boolean(
    selectedItems && selectedItems.data.length > 0,
  );

  /**
   * Handle clicks on links within the markdown content.
   * If a link is whitelisted (has data-in-app-route attribute),
   * navigate using React Router instead of opening in a new tab.
   */
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a");

    if (!link) {
      return;
    }

    const href = link.getAttribute("href") || "";

    if (link.hasAttribute("data-in-app-route")) {
      e.preventDefault();

      if (href) {
        try {
          // Parse the URL to extract pathname and search params
          const url = new URL(href, window.location.origin);
          const path = url.pathname + url.search + url.hash;
          history.push(path);
        } catch (error) {
          console.error("Failed to navigate to in-app route:", error);
        }
      }

      return;
    }

    if (onHelpCenterLinkClick && isInternalServiceUrl(href)) {
      e.preventDefault();
      onHelpCenterLinkClick(href);
    }
  };

  function renderContent() {
    if (role === "assistant") {
      if (!content) return null;
      return (
        <div className="chat-message__answer-row">
          <AIStarsIcon className="chat-message__avatar" />
          <div className="chat-message__content">
            <div
              className="chat-message__text chat-message__text--markdown"
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(content, memberUuid),
              }}
              onClick={handleLinkClick}
            />
            {showQuickPrompt && onQuickPrompt && (
              <button
                type="button"
                className="chat-message__quick-prompt"
                onClick={onQuickPrompt}
              >
                <AIStarsIcon className="chat-message__quick-prompt-icon" />
                Show all in Creator Search
              </button>
            )}
            {timestamp && (
              <div className="chat-message__timestamp">{timestamp}</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="chat-message__content">
        <div
          className="chat-message__text chat-message__text--markdown"
          dangerouslySetInnerHTML={{
            __html: renderMarkdown(content, memberUuid),
          }}
          onClick={handleLinkClick}
        />
        {(hasFiles || hasSelectedItems) && (
          <div className="chat-message__attachments">
            {hasSelectedItems && (
              <SelectedContextBadge selectedContext={selectedItems!} />
            )}
            {hasFiles &&
              attachedFiles!.map((file, index) => (
                <Attachment
                  key={`${file.name}-${index}`}
                  data={file}
                  place={AttachmentPlacement.InlineWithLabel}
                  taskType={TaskType.Content}
                />
              ))}
          </div>
        )}
        {timestamp && (
          <div className="chat-message__timestamp">{timestamp}</div>
        )}
      </div>
    );
  }

  return (
    <div className={classNames("chat-message", `chat-message--${role}`)}>
      {role === "assistant" && thinking && (
        <ThinkingBlock
          content={thinking}
          seconds={thinkingSeconds}
          isStreaming={isStreaming}
          memberUuid={memberUuid}
        />
      )}
      {renderContent()}
    </div>
  );
}
